import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Activity,
  Search,
  User,
  FileText,
  BarChart3,
  TrendingUp,
  TrendingDown,
  Minus,
  ChevronRight,
  ArrowLeft,
  Dumbbell,
  HelpCircle,
  X
} from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  Area,
  AreaChart
} from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { getExerciseType, EXERCISE_TYPES } from '../../utils/exerciseFormatters';
import { memberMatchesNameOrEmailSearch, normalizeMemberSearchInput } from '../../utils/memberSearchMatch';
import InfoTooltip from '../progress/Tooltip';

/** Query param per aprire direttamente le statistiche di un membro (es. da scheda cliente). Nome: memberId o user_id */
const MEMBER_ID_PARAM = 'memberId';

/** Accento grafici tema scuro (linea frequenza, istogrammi, filtri sett./mese) */
const STATS_CHART_VIOLET = '#6366f1';

export default function GymStatisticsIndividual() {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getAuthenticatedApi } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeTab, setActiveTab] = useState('generale');

  const memberIdFromUrl = searchParams.get(MEMBER_ID_PARAM) || searchParams.get('user_id');

  // Carica lista clienti (solo role member) per ricerca – una richiesta con limite alto (Statistiche = vedere tutti)
  useEffect(() => {
    let cancelled = false;
    const api = getAuthenticatedApi();
    setLoadingMembers(true);
    api
      .get('/gym_members.php?action=list&status=active&limit=1000')
      .then((res) => {
        if (cancelled) return;
        const list = res.data?.members ?? res.data?.clients ?? res.data ?? [];
        const clients = Array.isArray(list)
          ? list.filter((m) => (m.role_in_gym || m.role) === 'member' || !m.role_in_gym)
          : [];
        setMembers(clients);
      })
      .catch(() => {
        if (!cancelled) setMembers([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingMembers(false);
      });
    return () => { cancelled = true; };
  }, [getAuthenticatedApi]);

  // Apertura per membro specifico da URL (link dalla scheda cliente – WEBAPP-035)
  useEffect(() => {
    if (!memberIdFromUrl) return;
    const id = String(memberIdFromUrl).trim();
    if (!id) return;
    const found = members.find((m) => String(m.id || m.user_id) === id);
    if (found) {
      setSelectedMember(found);
      setSearchTerm(found.name || found.nome || found.email || '');
    } else if (!selectedMember) {
      setSelectedMember({ id: id, user_id: id, name: '', nome: t('gym.statistics.individual.memberIdFallback', { id }) });
      setSearchTerm('');
    }
  }, [memberIdFromUrl, members, selectedMember, t]);

  // Filtra clienti per nome/email (ricerca avanzata: almeno 2 caratteri)
  const filteredMembers = useMemo(() => {
    const q = normalizeMemberSearchInput(searchTerm);
    if (q.length < 2) return [];
    return members.filter((m) => memberMatchesNameOrEmailSearch(m, q));
  }, [members, searchTerm]);

  const openDropdown = filteredMembers.length > 0 && normalizeMemberSearchInput(searchTerm).length >= 2;

  const handleSelectMember = (member) => {
    setSelectedMember(member);
    setSearchTerm(member.name || member.nome || member.email || '');
    setShowDropdown(false);
    const id = member.id ?? member.user_id;
    if (id != null) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set(MEMBER_ID_PARAM, String(id));
        return next;
      }, { replace: true });
    }
  };

  const statisticsHeaderInner = (
    <>
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <div
          className={`shrink-0 rounded-xl p-2.5 ${
            isDark
              ? 'bg-gradient-to-br from-gray-800 to-gray-600'
              : 'bg-indigo-100 dark:bg-indigo-900/30'
          }`}
        >
          <Activity
            className={`h-8 w-8 ${isDark ? 'text-white' : 'text-indigo-600 dark:text-indigo-400'}`}
            aria-hidden
          />
        </div>
        <div className="min-w-0">
          <h1
            className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900 dark:text-white'}`}
          >
            {t('gym.navigation.statisticsIndividual')}
          </h1>
          <p className={`mt-0.5 text-sm ${isDark ? 'text-white/75' : 'text-gray-500 dark:text-gray-400'}`}>
            {t('gym.statistics.individualDesc')}
          </p>
        </div>
      </div>

      <div className="relative w-full shrink-0 lg:max-w-md">
        <div className="relative">
          <Search
            className={`absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 ${
              isDark ? 'text-gray-300' : 'text-gray-400 dark:text-gray-500'
            }`}
            aria-hidden
          />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setShowDropdown(true);
            }}
            onFocus={() => searchTerm.trim().length >= 2 && setShowDropdown(true)}
            onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
            placeholder={t('gym.statistics.individual.searchPlaceholder')}
            className={`w-full rounded-xl border py-3 pl-10 pr-4 focus:border-transparent focus:ring-2 ${
              isDark
                ? 'border-white/10 bg-gray-900 text-white placeholder:text-white/45 focus:ring-indigo-500'
                : 'border-gray-200 bg-white text-gray-900 placeholder-gray-500 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:placeholder-gray-500 dark:focus:ring-indigo-500'
            }`}
            aria-autocomplete="list"
            aria-expanded={openDropdown}
          />
        </div>
        {searchTerm.trim().length > 0 && searchTerm.trim().length < 2 && (
          <p
            className={`mt-1.5 text-xs ${isDark ? 'text-white/60' : 'text-gray-500 dark:text-gray-400'}`}
          >
            {t('gym.statistics.individual.searchMinChars')}
          </p>
        )}
        {showDropdown && openDropdown && (
          <ul
            className={`absolute left-0 right-0 z-10 mt-1 max-h-60 overflow-y-auto rounded-xl border py-1 shadow-lg ${
              isDark
                ? 'border-white/10 bg-gray-950 text-white'
                : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800 dark:text-white'
            }`}
            role="listbox"
          >
            {loadingMembers ? (
              <li className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">Caricamento...</li>
            ) : filteredMembers.length === 0 ? (
              <li className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                {t('gym.statistics.individual.noResults')}
              </li>
            ) : (
              filteredMembers.map((m) => (
                <li
                  key={m.id || m.user_id}
                  role="option"
                  onMouseDown={() => handleSelectMember(m)}
                  className={`flex cursor-pointer items-center gap-3 px-4 py-2.5 ${
                    isDark
                      ? 'text-white hover:bg-white/10'
                      : 'text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-gray-700'
                  }`}
                >
                  <User className={`h-4 w-4 flex-shrink-0 ${isDark ? 'text-white/50' : 'text-gray-400'}`} />
                  <span className="font-medium">{m.name || m.nome || m.email || '—'}</span>
                  {m.email && m.name !== m.email && (
                    <span
                      className={`truncate text-sm ${isDark ? 'text-white/55' : 'text-gray-500 dark:text-gray-400'}`}
                    >
                      {m.email}
                    </span>
                  )}
                </li>
              ))
            )}
          </ul>
        )}
      </div>
    </>
  );

  const statisticsEmptyState = (
    <div
      className={`p-8 text-center ${
        isDark
          ? 'border-t border-white/10 text-white/85'
          : 'rounded-xl border border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-400'
      }`}
    >
      <User
        className={`mx-auto mb-3 h-12 w-12 ${isDark ? 'text-white/40' : 'text-gray-400 dark:text-gray-500'}`}
      />
      <p className={isDark ? 'text-white/85' : 'text-gray-600 dark:text-gray-400'}>
        {t('gym.statistics.individual.selectClient')}
      </p>
    </div>
  );

  const statisticsMemberToolbar =
    selectedMember && (
      <div
        className={`overflow-hidden ${
          isDark
            ? 'w-full shrink-0 border-b border-white/10 bg-gray-900 py-3'
            : 'mb-6 rounded-xl border border-gray-200 dark:border-gray-700'
        }`}
      >
        <div
          className={`flex flex-wrap items-center gap-2 py-3 text-sm ${
            isDark ? 'px-fluid-lg text-white/80 sm:px-fluid-xl' : 'px-4 text-gray-600 dark:text-gray-400'
          }`}
        >
          <User className="h-4 w-4 shrink-0" />
          <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900 dark:text-white'}`}>
            {selectedMember.name || selectedMember.nome || selectedMember.email}
          </span>
          <button
            type="button"
            onClick={() => {
              setSelectedMember(null);
              setSearchTerm('');
              setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.delete(MEMBER_ID_PARAM);
                next.delete('user_id');
                return next;
              }, { replace: true });
            }}
            className={
              isDark
                ? 'text-[#c9a8ff] hover:underline'
                : 'text-indigo-600 hover:underline dark:text-indigo-400'
            }
          >
            {t('gym.statistics.individual.changeClient')}
          </button>
        </div>

        <nav
          className={`flex gap-1 border-t pb-0 pt-2 ${
            isDark
              ? 'border-white/10 px-fluid-lg sm:px-fluid-xl'
              : 'border-gray-200 px-2 dark:border-gray-700'
          }`}
          aria-label="Tabs"
        >
          <button
            type="button"
            onClick={() => setActiveTab('generale')}
            className={`rounded-t-lg px-4 py-2.5 text-sm font-medium transition-colors ${
              activeTab === 'generale'
                ? isDark
                  ? 'bg-indigo-600 text-white shadow-sm'
                  : 'bg-indigo-600 text-white'
                : isDark
                  ? 'text-white/65 hover:bg-white/10 hover:text-white'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white'
            }`}
          >
            {t('gym.statistics.individual.tabGenerale')}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('schede')}
            className={`rounded-t-lg px-4 py-2.5 text-sm font-medium transition-colors ${
              activeTab === 'schede'
                ? isDark
                  ? 'bg-indigo-600 text-white shadow-sm'
                  : 'bg-indigo-600 text-white'
                : isDark
                  ? 'text-white/65 hover:bg-white/10 hover:text-white'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white'
            }`}
          >
            {t('gym.statistics.individual.tabSchede')}
          </button>
        </nav>
      </div>
    );

  const statisticsTabPanels =
    selectedMember &&
    (activeTab === 'generale' ? (
      <TabGenerale userId={selectedMember.id || selectedMember.user_id} />
    ) : activeTab === 'schede' ? (
      <TabSchede userId={selectedMember.id || selectedMember.user_id} />
    ) : null);

  const paddedMainClass = 'box-border max-w-full px-fluid-lg pb-fluid-lg sm:px-fluid-xl';

  return (
    <div
      className={
        isDark
          ? 'box-border flex min-h-full w-full min-w-0 flex-col bg-gray-950'
          : 'box-border w-full min-w-0'
      }
    >
      {isDark ? (
        <>
          <header className="w-full shrink-0 border-b border-white/10 bg-gray-900 pt-fluid-md pb-5">
            <div className="flex w-full min-w-0 max-w-none flex-col gap-4 px-fluid-lg sm:px-fluid-xl lg:flex-row lg:items-center lg:justify-between lg:gap-8">
              {statisticsHeaderInner}
            </div>
          </header>
          {!selectedMember ? (
            <div className={paddedMainClass}>{statisticsEmptyState}</div>
          ) : (
            <>
              {statisticsMemberToolbar}
              <div className={`${paddedMainClass} pt-fluid-md`}>{statisticsTabPanels}</div>
            </>
          )}
        </>
      ) : (
        <div className="p-fluid-md">
          <header className="mb-6 flex flex-col gap-4 rounded-xl lg:flex-row lg:items-center lg:justify-between lg:gap-8">
            {statisticsHeaderInner}
          </header>
          {!selectedMember ? statisticsEmptyState : (
            <>
              {statisticsMemberToolbar}
              {statisticsTabPanels}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/** Punteggio 0-100 per ACWR: zona sicura 0.8-1.3 = 100, >1.5 o <0.5 = 0 */
function acwrScore(acwr) {
  if (acwr == null || acwr <= 0) return null;
  if (acwr >= 0.8 && acwr <= 1.3) return 100;
  if (acwr >= 1.5 || acwr <= 0.5) return 0;
  if (acwr < 0.8) return Math.round(50 + (acwr - 0.5) * (50 / 0.3));
  return Math.round(100 - (acwr - 1.3) * (50 / 0.2));
}

/** Zona continuità 0–100 (allineata ai segmenti del tachimetro) */
function getConsistencyZoneKey(pct) {
  if (pct == null || Number.isNaN(Number(pct))) return null;
  const p = Math.min(100, Math.max(0, Number(pct)));
  if (p >= 80) return 'strongHabit';
  if (p >= 60) return 'good';
  if (p >= 40) return 'unstable';
  if (p >= 20) return 'risk';
  return 'lost';
}

/** Zona progressione carico: U = rapporto × 100 (ultime 4 sett. vs 4 precedenti) */
function getProgressionZoneKey(U) {
  if (U == null || Number.isNaN(Number(U))) return null;
  if (U < 85) return 'dropSevere';
  if (U < 95) return 'dropModerate';
  if (U <= 105) return 'stable';
  if (U < 120) return 'growth';
  return 'growthStrong';
}

/**
 * Posizione ago 0–100 = scala letta sul tachimetro (allineata alle tacche 0 / 50 / 100).
 * U = rapporto volumi ×100 (100 = parità tra i due periodi).
 * -20% vs parità → U=80 → ago a 0; parità U=100 → ago a 100; U>100 → ago al massimo (100).
 */
function progressionLoadToNeedlePercent(U) {
  if (U == null || Number.isNaN(Number(U))) return null;
  const u = Number(U);
  const p = ((u - 80) / 20) * 100;
  return Math.min(100, Math.max(0, p));
}

/** Punto su arco superiore: angolo 0 = destra, π = sinistra (SVG y verso il basso) */
function polarToXY(cx, cy, r, angleRad) {
  return {
    x: cx + r * Math.cos(angleRad),
    y: cy - r * Math.sin(angleRad)
  };
}

function describeArcPath(cx, cy, r, startAngle, endAngle) {
  const start = polarToXY(cx, cy, r, startAngle);
  const end = polarToXY(cx, cy, r, endAngle);
  const delta = Math.abs(startAngle - endAngle);
  const largeArc = delta > Math.PI ? 1 : 0;
  /* sweep=1: arco lungo il semicerchio superiore (senso orario in SVG) */
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 1 ${end.x} ${end.y}`;
}

/** Tachimetro continuità: semicerchio 0–100% con segmenti colore e ago */
function ConsistencyGauge({ value, compact = false }) {
  const { t } = useTranslation();
  const cx = 100;
  const cy = 100;
  const r = compact ? 72 : 78;
  const strokeW = compact ? 12 : 14;
  const hasValue = value != null && !Number.isNaN(Number(value));
  const pct = hasValue ? Math.min(100, Math.max(0, Number(value))) : null;
  const zoneKey = getConsistencyZoneKey(pct);
  /** Angolo in radianti per percentuale p (0 sinistra → π, 100 destra → 0) */
  const angleForPct = (p) => Math.PI * (1 - p / 100);

  const segments = [
    { from: 0, to: 20, color: '#dc2626' },
    { from: 20, to: 40, color: '#ea580c' },
    { from: 40, to: 60, color: '#ca8a04' },
    { from: 60, to: 80, color: '#86efac' },
    { from: 80, to: 100, color: '#16a34a' }
  ];

  const needleAngle = pct != null ? angleForPct(pct) : Math.PI;
  const needleLen = r - 4;
  const nx = cx + needleLen * Math.cos(needleAngle);
  const ny = cy - needleLen * Math.sin(needleAngle);

  const zoneTextClass =
    zoneKey === 'strongHabit'
      ? 'text-green-600 dark:text-green-400'
      : zoneKey === 'good'
        ? 'text-lime-600 dark:text-lime-400'
        : zoneKey === 'unstable'
          ? 'text-yellow-600 dark:text-yellow-400'
          : zoneKey === 'risk'
            ? 'text-orange-600 dark:text-orange-400'
            : zoneKey === 'lost'
              ? 'text-red-600 dark:text-red-400'
              : 'text-gray-500 dark:text-gray-400';

  const ariaLabel =
    hasValue && zoneKey
      ? `${String(pct).replace(/\.0$/, '')}% ${t(`gym.statistics.individual.generale.consistencyZone.${zoneKey}`)}`
      : t('gym.statistics.individual.generale.consistencyNoData');

  return (
    <div className={`flex flex-col items-center w-full ${compact ? 'max-w-full' : 'max-w-sm mx-auto'}`}>
      <svg
        viewBox="0 0 200 118"
        className={`w-full h-auto ${compact ? 'max-h-[110px]' : 'max-h-[160px]'}`}
        role="img"
        aria-label={ariaLabel}
      >
        {segments.map((seg) => (
          <path
            key={`${seg.from}-${seg.to}`}
            d={describeArcPath(cx, cy, r, angleForPct(seg.from), angleForPct(seg.to))}
            fill="none"
            stroke={seg.color}
            strokeWidth={strokeW}
            strokeLinecap="butt"
          />
        ))}
        {/* Ago */}
        {hasValue && (
          <line
            x1={cx}
            y1={cy}
            x2={nx}
            y2={ny}
            stroke="currentColor"
            strokeWidth={3}
            strokeLinecap="round"
            className="text-gray-900 dark:text-gray-100"
          />
        )}
        <circle cx={cx} cy={cy} r={7} className="fill-gray-900 dark:fill-white" />
        {/* Tacche 0 / 50 / 100 */}
        {[0, 50, 100].map((mark) => {
          const a = angleForPct(mark);
          const i0 = polarToXY(cx, cy, r + strokeW / 2 + 2, a);
          const i1 = polarToXY(cx, cy, r + strokeW / 2 + 8, a);
          return (
            <line key={mark} x1={i0.x} y1={i0.y} x2={i1.x} y2={i1.y} stroke="currentColor" strokeWidth={1} className="text-gray-400 dark:text-gray-500" />
          );
        })}
      </svg>
      <div className={`text-center space-y-0.5 ${compact ? '-mt-0.5' : '-mt-1'}`}>
        <p className={`${compact ? 'text-lg' : 'text-2xl'} font-bold tabular-nums leading-tight ${zoneTextClass}`}>
          {hasValue ? `${pct.toFixed(1).replace(/\.0$/, '')}%` : '—'}
        </p>
        {zoneKey && (
          <p className={`${compact ? 'text-[11px] leading-snug' : 'text-sm'} font-semibold ${zoneTextClass}`}>
            {t(`gym.statistics.individual.generale.consistencyZone.${zoneKey}`)}
          </p>
        )}
        {!hasValue && (
          <p className={`${compact ? 'text-[10px]' : 'text-xs'} text-gray-500 dark:text-gray-400`}>
            {t('gym.statistics.individual.generale.consistencyNoData')}
          </p>
        )}
      </div>
    </div>
  );
}

/** Tachimetro progressione carico: tacche 0–100 = stessa scala del numero mostrato (vedi progressionLoadToNeedlePercent) */
function ProgressionGauge({ ratio, compact = false }) {
  const { t } = useTranslation();
  const cx = 100;
  const cy = 100;
  const r = compact ? 72 : 78;
  const strokeW = compact ? 12 : 14;
  const hasValue = ratio != null && !Number.isNaN(Number(ratio));
  const U = hasValue ? Math.round(Number(ratio) * 100) : null;
  const needleP = U != null ? progressionLoadToNeedlePercent(U) : null;
  const gaugeDisplayPct = needleP;
  const zoneKey = U != null ? getProgressionZoneKey(U) : null;
  const angleForPct = (p) => Math.PI * (1 - p / 100);

  const segments = [
    { from: 0, to: 20, color: '#dc2626' },
    { from: 20, to: 40, color: '#ea580c' },
    { from: 40, to: 60, color: '#ca8a04' },
    { from: 60, to: 80, color: '#86efac' },
    { from: 80, to: 100, color: '#16a34a' }
  ];

  const needleAngle = needleP != null ? angleForPct(needleP) : Math.PI;
  const needleLen = r - 4;
  const nx = cx + needleLen * Math.cos(needleAngle);
  const ny = cy - needleLen * Math.sin(needleAngle);

  const zoneTextClass =
    zoneKey === 'growthStrong'
      ? 'text-green-600 dark:text-green-400'
      : zoneKey === 'growth'
        ? 'text-lime-600 dark:text-lime-400'
        : zoneKey === 'stable'
          ? 'text-emerald-600 dark:text-emerald-400'
          : zoneKey === 'dropModerate'
            ? 'text-orange-600 dark:text-orange-400'
            : zoneKey === 'dropSevere'
              ? 'text-red-600 dark:text-red-400'
              : 'text-gray-500 dark:text-gray-400';

  const ariaLabel =
    hasValue && zoneKey && gaugeDisplayPct != null
      ? `${Math.round(gaugeDisplayPct)}% ${t(`gym.statistics.individual.generale.progressionZone.${zoneKey}`)}`
      : t('gym.statistics.individual.generale.progressionNoData');

  return (
    <div className={`flex flex-col items-center w-full ${compact ? 'max-w-full' : 'max-w-sm mx-auto'}`}>
      <svg
        viewBox="0 0 200 118"
        className={`w-full h-auto ${compact ? 'max-h-[110px]' : 'max-h-[160px]'}`}
        role="img"
        aria-label={ariaLabel}
      >
        {segments.map((seg) => (
          <path
            key={`${seg.from}-${seg.to}`}
            d={describeArcPath(cx, cy, r, angleForPct(seg.from), angleForPct(seg.to))}
            fill="none"
            stroke={seg.color}
            strokeWidth={strokeW}
            strokeLinecap="butt"
          />
        ))}
        {hasValue && (
          <line
            x1={cx}
            y1={cy}
            x2={nx}
            y2={ny}
            stroke="currentColor"
            strokeWidth={3}
            strokeLinecap="round"
            className="text-gray-900 dark:text-gray-100"
          />
        )}
        <circle cx={cx} cy={cy} r={7} className="fill-gray-900 dark:fill-white" />
        {[0, 50, 100].map((mark) => {
          const a = angleForPct(mark);
          const i0 = polarToXY(cx, cy, r + strokeW / 2 + 2, a);
          const i1 = polarToXY(cx, cy, r + strokeW / 2 + 8, a);
          return (
            <line key={mark} x1={i0.x} y1={i0.y} x2={i1.x} y2={i1.y} stroke="currentColor" strokeWidth={1} className="text-gray-400 dark:text-gray-500" />
          );
        })}
      </svg>
      <div className={`text-center space-y-0.5 ${compact ? '-mt-0.5' : '-mt-1'}`}>
        <p className={`${compact ? 'text-lg' : 'text-2xl'} font-bold tabular-nums leading-tight ${zoneTextClass}`}>
          {hasValue && gaugeDisplayPct != null
            ? `${gaugeDisplayPct % 1 === 0 ? Math.round(gaugeDisplayPct) : gaugeDisplayPct.toFixed(1)}%`
            : '—'}
        </p>
        {zoneKey && (
          <p className={`${compact ? 'text-[11px] leading-snug' : 'text-sm'} font-semibold ${zoneTextClass}`}>
            {t(`gym.statistics.individual.generale.progressionZone.${zoneKey}`)}
          </p>
        )}
        {!hasValue && (
          <p className={`${compact ? 'text-[10px]' : 'text-xs'} text-gray-500 dark:text-gray-400`}>
            {t('gym.statistics.individual.generale.progressionNoData')}
          </p>
        )}
      </div>
    </div>
  );
}

/** Tab Generale: valore complessivo atleta (ACWR, indice, progressione carico; senza lista esercizi) */
function TabGenerale({ userId }) {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { getAuthenticatedApi } = useAuth();
  const chartAccent = STATS_CHART_VIOLET;
  const chartAxisFill = isDark ? '#d1d5db' : '#6b7280';
  const chartGridStroke = isDark ? 'rgba(255,255,255,0.14)' : '#e5e7eb';
  const tooltipStyle = isDark
    ? { backgroundColor: '#1f1a3d', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8, color: '#fff' }
    : undefined;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [frequencyGranularity, setFrequencyGranularity] = useState('weekly'); // 'weekly' | 'monthly'

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getAuthenticatedApi()
      .get(`/gym_statistics_api.php?action=member_overview&user_id=${userId}&days=90`)
      .then((res) => {
        if (cancelled) return;
        if (res.data?.success && res.data?.data) setData(res.data.data);
        else setData(null);
      })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.error || err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [userId, getAuthenticatedApi]);

  const frequencyChartData = useMemo(() => {
    if (!data) return [];
    const fw = data.frequency_weekly;
    const fm = data.frequency_monthly;
    if (frequencyGranularity === 'weekly' && Array.isArray(fw)) {
      return fw.map((w) => ({
        ...w,
        xLabel: t('gym.statistics.individual.generale.frequencyWeekAxis', {
          week: w.iso_week != null ? w.iso_week : '—'
        })
      }));
    }
    if (frequencyGranularity === 'monthly' && Array.isArray(fm)) {
      return fm.map((m) => ({
        ...m,
        xLabel: new Date(`${m.year_month}-01T12:00:00`).toLocaleDateString('it-IT', { month: 'short', year: 'numeric' })
      }));
    }
    return [];
  }, [frequencyGranularity, data, t]);

  if (loading) {
    return (
      <div className="space-y-6 pt-2">
        <div
          className={`h-48 animate-pulse rounded-xl border ${
            isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50'
          }`}
        />
        <div
          className={`h-64 animate-pulse rounded-xl border ${
            isDark ? 'border-white/10 bg-gray-900/80' : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50'
          }`}
        />
      </div>
    );
  }
  if (error) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200">
        {error}
      </div>
    );
  }
  if (!data) {
    return (
      <div
        className={`p-8 text-center ${
          isDark
            ? 'rounded-xl border border-white/10 bg-white/5 text-white/70'
            : 'rounded-xl border border-gray-200 bg-gray-50 text-gray-500 dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-400'
        }`}
      >
        {t('gym.statistics.noData')}
      </div>
    );
  }

  const {
    frequency_per_week,
    consistency_index,
    volume_trend,
    acwr,
    acute_volume,
    chronic_volume_avg,
    volume_progression_ratio
  } = data;

  const consistency = consistency_index != null ? Math.min(100, Math.max(0, consistency_index)) : 50;
  const freqScore = frequency_per_week != null
    ? Math.min(100, Math.round((frequency_per_week / 4) * 100))
    : 50;
  const acwrSc = acwrScore(acwr);
  const athleteIndex = acwrSc != null
    ? Math.round(consistency * 0.4 + freqScore * 0.3 + acwrSc * 0.3)
    : Math.round(consistency * 0.5 + freqScore * 0.5);
  const indexClamped = Math.min(100, Math.max(0, athleteIndex));
  const indexColor = indexClamped >= 70 ? '#10b981' : indexClamped >= 40 ? '#d97706' : '#ef4444';

  return (
    <div className={`space-y-6 ${isDark ? 'pt-2' : ''}`}>
      <div
        className={`overflow-hidden ${
          isDark
            ? 'border-0 bg-transparent'
            : 'rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800'
        }`}
      >
        <div
          className={`px-4 py-3 ${isDark ? 'border-b border-white/10' : 'border-b border-gray-100 dark:border-gray-700'}`}
        >
          <h2 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900 dark:text-white'}`}>
            {t('gym.statistics.individual.generale.title')}
          </h2>
        </div>
        <div className={isDark ? 'px-0 py-6 sm:px-1' : 'p-6'}>
          <div className="mb-6 flex flex-col gap-6 sm:flex-row sm:items-center">
            <div className="flex items-center justify-center sm:justify-start">
              <div
                className="relative flex h-28 w-28 items-center justify-center rounded-full text-3xl font-bold text-white"
                style={{
                  background: `conic-gradient(${indexColor} 0deg ${indexClamped * 3.6}deg, ${isDark ? '#3d355f' : '#e5e7eb'}  ${indexClamped * 3.6}deg 360deg)`
                }}
              >
                <div
                  className={`absolute inset-1.5 flex items-center justify-center rounded-full ${
                    isDark ? 'bg-gray-800' : 'bg-white dark:bg-gray-800'
                  }`}
                >
                  <span
                    className={`text-2xl font-bold tabular-nums ${isDark ? 'text-white' : 'text-gray-900 dark:text-white'}`}
                  >
                    {indexClamped}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex-1">
              <h3 className={`text-base font-semibold ${isDark ? 'text-white' : 'text-gray-900 dark:text-white'}`}>
                {t('gym.statistics.individual.generale.athleteIndex')}
              </h3>
              <p className={`mt-1 text-sm ${isDark ? 'text-white/70' : 'text-gray-500 dark:text-gray-400'}`}>
                {t('gym.statistics.individual.generale.athleteIndexHint')}
              </p>
            </div>
          </div>

          <div
            className={`mb-6 rounded-xl border p-4 ${
              isDark ? 'border-white/10 bg-gray-900' : 'border-gray-200 bg-gray-50/80 dark:border-gray-700 dark:bg-gray-900/40'
            }`}
          >
            <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h3 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900 dark:text-white'}`}>
                {t('gym.statistics.individual.generale.frequencyChartTitle')}
              </h3>
              <div
                className={`inline-flex rounded-lg border p-0.5 ${
                  isDark ? 'border-white/15 bg-gray-800' : 'border-gray-200 bg-white dark:border-gray-600 dark:bg-gray-800'
                }`}
              >
                <button
                  type="button"
                  onClick={() => setFrequencyGranularity('weekly')}
                  className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                    frequencyGranularity === 'weekly'
                      ? isDark
                        ? 'bg-indigo-600 text-white shadow-sm'
                        : 'bg-indigo-600 text-white shadow-sm'
                      : isDark
                        ? 'text-white/70 hover:text-white'
                        : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'
                  }`}
                >
                  {t('gym.statistics.individual.generale.frequencyWeekly')}
                </button>
                <button
                  type="button"
                  onClick={() => setFrequencyGranularity('monthly')}
                  className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                    frequencyGranularity === 'monthly'
                      ? isDark
                        ? 'bg-indigo-600 text-white shadow-sm'
                        : 'bg-indigo-600 text-white shadow-sm'
                      : isDark
                        ? 'text-white/70 hover:text-white'
                        : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'
                  }`}
                >
                  {t('gym.statistics.individual.generale.frequencyMonthly')}
                </button>
              </div>
            </div>
            {frequencyChartData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={frequencyChartData} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={chartGridStroke} />
                    <XAxis
                      dataKey="xLabel"
                      tick={{ fontSize: 10, fill: chartAxisFill }}
                      interval={0}
                      angle={frequencyGranularity === 'weekly' ? -25 : -18}
                      textAnchor={frequencyGranularity === 'weekly' ? 'end' : 'end'}
                      height={frequencyGranularity === 'weekly' ? 52 : 48}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: chartAxisFill }}
                      allowDecimals={false}
                      domain={[0, 'auto']}
                    />
                    <Tooltip
                      contentStyle={tooltipStyle}
                      formatter={(value) => [value, t('gym.statistics.individual.generale.frequencyTooltipSessions')]}
                      labelFormatter={(label, payload) => {
                        if (payload && payload[0]?.payload?.week_start && payload[0]?.payload?.week_end) {
                          const a = new Date(`${payload[0].payload.week_start}T12:00:00`).toLocaleDateString('it-IT');
                          const b = new Date(`${payload[0].payload.week_end}T12:00:00`).toLocaleDateString('it-IT');
                          return `${a} – ${b}`;
                        }
                        if (payload && payload[0]?.payload?.year_month) {
                          return new Date(`${payload[0].payload.year_month}-01T12:00:00`).toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });
                        }
                        return label;
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="count"
                      name={t('gym.statistics.individual.generale.frequencyTooltipSessions')}
                      stroke={isDark ? chartAccent : '#6366f1'}
                      strokeWidth={2}
                      dot={{ r: 4, fill: isDark ? chartAccent : '#6366f1' }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
                <p className={`mt-2 text-xs ${isDark ? 'text-white/60' : 'text-gray-500 dark:text-gray-400'}`}>
                  {t('gym.statistics.individual.generale.frequencyYAxis')}
                </p>
              </>
            ) : (
              <div
                className={`flex h-[220px] items-center justify-center rounded-lg border border-dashed text-sm ${
                  isDark
                    ? 'border-white/20 text-white/55'
                    : 'border-gray-300 text-gray-500 dark:border-gray-600 dark:text-gray-400'
                }`}
              >
                {t('gym.statistics.individual.generale.noFrequencyData')}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
            <div
              className={`flex min-h-0 flex-col rounded-xl border p-2 sm:p-3 ${
                isDark ? 'border-white/10 bg-gray-900' : 'border-gray-200 bg-gray-50/80 dark:border-gray-700 dark:bg-gray-900/40'
              }`}
            >
              <p
                className={`mb-1 flex shrink-0 items-center justify-center gap-1 text-[10px] uppercase tracking-wide ${
                  isDark ? 'text-white/60' : 'text-gray-500 dark:text-gray-400'
                }`}
              >
                {t('gym.statistics.individual.generale.metrics.consistency')}
                <InfoTooltip text={t('gym.statistics.individual.generale.tooltipConsistency')} position="top" />
              </p>
              <div className="flex-1 flex items-start justify-center min-h-[140px] sm:min-h-[150px]">
                <ConsistencyGauge compact value={consistency_index} />
              </div>
            </div>
            <div
              className={`flex min-h-0 flex-col rounded-xl border p-2 sm:p-3 ${
                isDark ? 'border-white/10 bg-gray-900' : 'border-gray-200 bg-gray-50/80 dark:border-gray-700 dark:bg-gray-900/40'
              }`}
            >
              <p
                className={`mb-1 flex shrink-0 items-center justify-center gap-1 text-[10px] uppercase tracking-wide ${
                  isDark ? 'text-white/60' : 'text-gray-500 dark:text-gray-400'
                }`}
              >
                {t('gym.statistics.individual.generale.progressionGaugeTitle')}
                <InfoTooltip text={t('gym.statistics.individual.generale.tooltipProgression')} position="top" />
              </p>
              <div className="flex-1 flex items-start justify-center min-h-[140px] sm:min-h-[160px]">
                <ProgressionGauge compact ratio={volume_progression_ratio} />
              </div>
            </div>
          </div>

          {data.uses_closure_days && (
            <p className={`mt-2 text-xs ${isDark ? 'text-white/55' : 'text-gray-500 dark:text-gray-400'}`}>
              {t('gym.statistics.individual.generale.closureNote')}
            </p>
          )}

          <details
            className={`mt-4 rounded-lg border ${
              isDark ? 'border-white/10 bg-gray-900/90' : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900/30'
            }`}
          >
            <summary
              className={`flex cursor-pointer list-none items-center gap-2 px-4 py-2.5 text-sm font-medium ${
                isDark ? 'text-white/90' : 'text-gray-700 dark:text-gray-300'
              }`}
            >
              <HelpCircle
                className={`h-4 w-4 shrink-0 ${isDark ? 'text-[#b894ff]' : 'text-indigo-500'}`}
              />
              <span>{t('gym.statistics.individual.generale.howToReadTitle')}</span>
            </summary>
            <div
              className={`mt-1 space-y-2 border-t px-4 pb-3 pt-1 text-xs ${
                isDark ? 'border-white/10 text-white/70' : 'border-gray-200 text-gray-600 dark:border-gray-700 dark:text-gray-400'
              }`}
            >
              <p><strong>Progressione carico:</strong> {t('gym.statistics.individual.generale.progressionExplain')}</p>
              <p><strong>Grafico:</strong> {t('gym.statistics.individual.generale.howToReadChart')}</p>
              {data.uses_closure_days && (
                <p><strong>Frequenza e continuità:</strong> {t('gym.statistics.individual.generale.closureNote')}</p>
              )}
            </div>
          </details>

          <div className="mt-6">
            <p
              className={`mb-2 flex items-center gap-1 text-xs font-medium ${
                isDark ? 'text-white/75' : 'text-gray-600 dark:text-gray-400'
              }`}
            >
              <TrendingUp className="h-3.5 w-3.5" />
              {t('gym.statistics.individual.generale.metrics.progressiveOverload')}
            </p>
            {volume_trend && volume_trend.length > 0 && volume_trend.some((w) => (w.volume ?? 0) > 0) ? (
              <div
                className={`rounded-xl border p-3 ${
                  isDark ? 'border-white/10 bg-gray-900' : 'border-transparent bg-transparent'
                }`}
              >
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={volume_trend} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={chartGridStroke} />
                    <XAxis dataKey="week_label" tick={{ fontSize: 10, fill: chartAxisFill }} />
                    <YAxis tick={{ fontSize: 10, fill: chartAxisFill }} allowDecimals={false} domain={[0, 'auto']} />
                    <Tooltip
                      contentStyle={tooltipStyle}
                      formatter={(v) => [v != null ? Math.round(Number(v)).toLocaleString() : '—', 'Volume (kg)']}
                    />
                    <Bar
                      dataKey="volume"
                      fill={isDark ? chartAccent : '#6366f1'}
                      radius={[2, 2, 0, 0]}
                      name="Volume"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div
                className={`flex h-[200px] items-center justify-center rounded-lg border border-dashed ${
                  isDark
                    ? 'border-white/20 bg-gray-900/50'
                    : 'border-gray-300 bg-gray-50 dark:border-gray-600 dark:bg-gray-900/30'
                }`}
              >
                <p
                  className={`px-4 text-center text-sm ${isDark ? 'text-white/55' : 'text-gray-500 dark:text-gray-400'}`}
                >
                  {t('gym.statistics.individual.generale.noVolumeData')}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Tab Schede: scegli scheda → vista esperta con grafico per ogni esercizio */
function TabSchede({ userId }) {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { getAuthenticatedApi } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedScheda, setSelectedScheda] = useState(null);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSelectedScheda(null);
    getAuthenticatedApi()
      .get(`/gym_statistics_api.php?action=member_schede&user_id=${userId}`)
      .then((res) => {
        if (cancelled) return;
        if (res.data?.success && res.data?.data?.schede) setData(res.data.data);
        else setData({ schede: [] });
      })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.error || err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [userId, getAuthenticatedApi]);

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 h-48 animate-pulse" />
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 h-64 animate-pulse" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4 text-red-700 dark:text-red-300">
        {error}
      </div>
    );
  }

  const schede = data?.schede ?? [];

  // Vista: scegli scheda (lista card cliccabili)
  if (!selectedScheda) {
    return (
      <div className="space-y-6">
        <p className={`text-sm ${isDark ? 'text-white/70' : 'text-gray-600 dark:text-gray-400'}`}>
          {t('gym.statistics.individual.schede.selectSchedaHint')}
        </p>
        {schede.length === 0 ? (
          <div
            className={`rounded-xl border p-8 text-center ${
              isDark
                ? 'border-white/10 bg-gray-900/60 text-white/65'
                : 'border-gray-200 bg-gray-50 text-gray-500 dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-400'
            }`}
          >
            {t('gym.statistics.noData')} ({t('gym.statistics.individual.schede.title')})
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {schede.map((scheda) => {
              const exerciseCount = scheda.exercises?.length ?? 0;
              const withData = scheda.exercises?.filter((e) => e.trend?.length > 0).length ?? 0;
              return (
                <button
                  key={scheda.scheda_id}
                  type="button"
                  onClick={() => setSelectedScheda(scheda)}
                  className={`flex items-center justify-between gap-4 rounded-xl border-2 p-4 text-left transition-all hover:shadow-lg ${
                    isDark
                      ? 'border-white/10 bg-gray-900 hover:border-indigo-500'
                      : 'border-gray-200 bg-white hover:border-indigo-500 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-indigo-500'
                  }`}
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <div
                      className={`flex-shrink-0 rounded-lg p-2 ${
                        isDark ? 'bg-gradient-to-br from-gray-800 to-gray-600' : 'bg-indigo-100 dark:bg-indigo-900/30'
                      }`}
                    >
                      <FileText
                        className={`h-5 w-5 ${isDark ? 'text-white' : 'text-indigo-600 dark:text-indigo-400'}`}
                      />
                    </div>
                    <div className="min-w-0">
                      <h3
                        className={`truncate font-semibold ${isDark ? 'text-white' : 'text-gray-900 dark:text-white'}`}
                      >
                        {scheda.scheda_nome}
                      </h3>
                      <p className={`mt-0.5 text-xs ${isDark ? 'text-white/60' : 'text-gray-500 dark:text-gray-400'}`}>
                        {exerciseCount} esercizi · dati in {withData}/{exerciseCount}
                      </p>
                      {scheda.assigned_date && (
                        <p className={`mt-0.5 text-xs ${isDark ? 'text-white/45' : 'text-gray-400 dark:text-gray-500'}`}>
                          Assegnata {new Date(scheda.assigned_date).toLocaleDateString('it-IT')}
                        </p>
                      )}
                    </div>
                  </div>
                  <ChevronRight
                    className={`h-5 w-5 flex-shrink-0 ${isDark ? 'text-white/40' : 'text-gray-400 dark:text-gray-500'}`}
                  />
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // Vista esperta: analisi scheda selezionata (grafico per ogni esercizio)
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => setSelectedScheda(null)}
          className={`rounded-lg p-2 ${
            isDark ? 'text-white/75 hover:bg-white/10' : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
          }`}
          aria-label={t('gym.statistics.individual.schede.backToSchede')}
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900 dark:text-white'}`}>
            {selectedScheda.scheda_nome}
          </h2>
          {selectedScheda.assigned_date && (
            <p className={`text-sm ${isDark ? 'text-white/65' : 'text-gray-500 dark:text-gray-400'}`}>
              Assegnata il {new Date(selectedScheda.assigned_date).toLocaleDateString('it-IT')}
            </p>
          )}
        </div>
      </div>

      {(!selectedScheda.exercises || selectedScheda.exercises.length === 0) ? (
        <div
          className={`rounded-xl border p-8 text-center ${
            isDark
              ? 'border-white/10 bg-gray-900/60 text-white/65'
              : 'border-gray-200 bg-gray-50 text-gray-500 dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-400'
          }`}
        >
          Nessun esercizio in questa scheda.
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {selectedScheda.exercises.map((ex) => (
            <ExerciseTrendCard key={ex.scheda_esercizio_id} exercise={ex} userId={userId} />
          ))}
        </div>
      )}
    </div>
  );
}

/** Formattazione cella matrice serie × sessione */
function formatMatrixCell(cell, exerciseMeta) {
  if (!cell) return '—';
  const type = getExerciseType(exerciseMeta);
  if (type === EXERCISE_TYPES.CARDIO) {
    const sec = cell.ripetizioni ?? 0;
    return `${Math.max(1, Math.round(sec / 60))} min`;
  }
  if (type === EXERCISE_TYPES.ISOMETRIC) {
    let s = `${cell.ripetizioni ?? 0} s`;
    if (cell.is_rest_pause && cell.rest_pause_reps) s += ` (${cell.rest_pause_reps})`;
    return s;
  }
  const w = cell.peso ?? 0;
  const r = cell.ripetizioni ?? 0;
  let s =
    w > 0
      ? `${Number(w).toLocaleString('it-IT', { maximumFractionDigits: 2 })}×${r}`
      : `${r} rip`;
  if (cell.is_rest_pause) {
    if (cell.rest_pause_reps) s += ` (${cell.rest_pause_reps})`;
    s += ' RP';
  }
  return s;
}

/** Metrica numerica confrontabile tra celle (volume, secondi, ecc.) */
function getComparableMetric(cell, exerciseMeta) {
  if (!cell) return null;
  const type = getExerciseType(exerciseMeta);
  if (type === EXERCISE_TYPES.CARDIO || type === EXERCISE_TYPES.ISOMETRIC) {
    const v = cell.ripetizioni ?? 0;
    return v > 0 ? v : null;
  }
  const vol = cell.volume ?? (Number(cell.peso) || 0) * (Number(cell.ripetizioni) || 0);
  if (vol > 0) return vol;
  const reps = Number(cell.ripetizioni) || 0;
  /* Corpo libero: volume spesso 0 in DB → confrontiamo le ripetizioni */
  return reps > 0 ? reps : null;
}

function computeMatrixInsights(matrixData, exMeta) {
  if (!matrixData?.workouts?.length || !matrixData?.rows?.length) return null;
  const W = matrixData.workouts.length;
  const sessionTotals = [];
  for (let c = 0; c < W; c++) {
    let sum = 0;
    let any = false;
    for (const row of matrixData.rows) {
      const cell = row.cells[c];
      const m = getComparableMetric(cell, exMeta);
      if (m != null) {
        sum += m;
        any = true;
      }
    }
    sessionTotals.push(any ? sum : 0);
  }
  const firstTot = sessionTotals[0];
  const lastTot = sessionTotals[W - 1];
  let pctSession = null;
  if (W >= 2) {
    if (firstTot > 0) {
      pctSession = ((lastTot - firstTot) / firstTot) * 100;
    } else if (lastTot > 0) {
      pctSession = 100;
    }
  }
  if (pctSession != null && !Number.isFinite(pctSession)) {
    pctSession = null;
  }
  let overallTrend = 'flat';
  if (pctSession != null) {
    if (pctSession > 3) overallTrend = 'up';
    else if (pctSession < -3) overallTrend = 'down';
  }

  const rowTrends = matrixData.rows.map((row) => {
    let firstIdx = -1;
    let lastIdx = -1;
    for (let i = 0; i < row.cells.length; i++) {
      if (getComparableMetric(row.cells[i], exMeta) != null) {
        if (firstIdx === -1) firstIdx = i;
        lastIdx = i;
      }
    }
    if (firstIdx === -1 || lastIdx === firstIdx) return 'flat';
    const a = getComparableMetric(row.cells[firstIdx], exMeta);
    const b = getComparableMetric(row.cells[lastIdx], exMeta);
    if (a == null || b == null) return 'flat';
    if (b > a * 1.02) return 'up';
    if (b < a * 0.98) return 'down';
    return 'flat';
  });

  return { sessionTotals, pctSession, overallTrend, rowTrends };
}

function cellDeltaClass(prevCell, cell, exMeta) {
  if (!prevCell || !cell) return '';
  const a = getComparableMetric(prevCell, exMeta);
  const b = getComparableMetric(cell, exMeta);
  if (a == null || b == null || a === 0) return '';
  const ch = (b - a) / a;
  if (ch > 0.02) return 'bg-emerald-50/90 dark:bg-emerald-950/35';
  if (ch < -0.02) return 'bg-rose-50/80 dark:bg-rose-950/30';
  return '';
}

function formatCellDeltaTitle(prevCell, cell, exMeta) {
  const a = getComparableMetric(prevCell, exMeta);
  const b = getComparableMetric(cell, exMeta);
  if (a == null || b == null || a === 0) return undefined;
  const ch = ((b - a) / a) * 100;
  if (Math.abs(ch) < 0.3) return undefined;
  const sign = ch > 0 ? '+' : '';
  return `${sign}${ch.toFixed(1)}% vs colonna precedente`;
}

/** Modal: tabella serie (righe) × allenamenti (colonne) */
function ExerciseSeriesDetailModal({ open, onClose, userId, exercise }) {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { getAuthenticatedApi } = useAuth();
  const [matrixData, setMatrixData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const scheda_esercizio_id = exercise?.scheda_esercizio_id;

  useEffect(() => {
    if (!open || !userId || !scheda_esercizio_id) return;
    let cancelled = false;
    setLoading(true);
    setError(false);
    setMatrixData(null);
    getAuthenticatedApi()
      .get(
        `/gym_statistics_api.php?action=member_exercise_series_matrix&user_id=${userId}&scheda_esercizio_id=${scheda_esercizio_id}`
      )
      .then((res) => {
        if (cancelled) return;
        if (res.data?.success && res.data?.data) setMatrixData(res.data.data);
        else setError(true);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, userId, scheda_esercizio_id, getAuthenticatedApi]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const exMeta = useMemo(() => {
    if (!matrixData?.exercise) return exercise;
    return {
      ...exercise,
      nome: matrixData.exercise.nome,
      is_cardio: matrixData.exercise.is_cardio,
      is_isometric: matrixData.exercise.is_isometric
    };
  }, [matrixData, exercise]);

  const insights = useMemo(
    () => (matrixData && exMeta ? computeMatrixInsights(matrixData, exMeta) : null),
    [matrixData, exMeta]
  );

  const sparkData = useMemo(() => {
    if (!insights || !matrixData?.workouts) return [];
    return matrixData.workouts.map((w, i) => ({
      label: w.date_label,
      total: Math.round((insights.sessionTotals[i] ?? 0) * 10) / 10
    }));
  }, [insights, matrixData]);

  if (!open) return null;

  const titleName = matrixData?.exercise?.nome ?? exercise?.nome ?? '';
  const sparkStroke = isDark ? STATS_CHART_VIOLET : '#372d83';
  const axisTick = isDark ? '#d1d5db' : '#3a3a3a';
  const gridStroke = isDark ? 'rgba(255,255,255,0.12)' : '#dad8e5';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 backdrop-blur-sm" role="dialog" aria-modal="true">
      <button
        type="button"
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-label={t('gym.statistics.individual.schede.closeModal')}
      />
      <div className="relative flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-[#dad8e5] bg-white shadow-2xl dark:border-white/10 dark:bg-gray-900 dark:shadow-black/35">
        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-[#dad8e5] bg-[#dad8e5] px-4 py-3 dark:border-white/10 dark:bg-gray-800">
          <h3 className="pr-2 text-base font-semibold text-[#3a3a3a] dark:text-white">
            {t('gym.statistics.individual.schede.seriesDetailTitle', { name: titleName })}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-[#3a3a3a] transition-colors hover:bg-black/5 dark:text-white/90 dark:hover:bg-white/10"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="border-b border-[#dad8e5] bg-[#faf9fc] px-4 pt-2 pb-2 text-xs text-[#3a3a3a]/75 dark:border-white/10 dark:bg-gray-900 dark:text-white/65">
          {t('gym.statistics.individual.schede.matrixHint')}
        </p>
        <div className="flex-1 overflow-auto bg-[#faf9fc] p-4 dark:bg-gray-900">
          {loading && (
            <p className="text-sm text-[#3a3a3a]/70 dark:text-white/60">{t('gym.statistics.individual.schede.matrixLoading')}</p>
          )}
          {error && !loading && (
            <p className="text-sm text-red-600 dark:text-red-300">{t('gym.statistics.individual.schede.matrixError')}</p>
          )}
          {!loading &&
            !error &&
            matrixData &&
            (!matrixData.workouts || matrixData.workouts.length === 0 || !matrixData.rows?.length) && (
              <p className="text-sm text-[#3a3a3a]/70 dark:text-white/60">{t('gym.statistics.individual.schede.matrixEmpty')}</p>
            )}
          {!loading && !error && matrixData && matrixData.workouts?.length > 0 && matrixData.rows?.length > 0 && insights && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div
                  className={`rounded-xl border p-4 ${
                    isDark
                      ? 'border-white/10 bg-gray-900 bg-gradient-to-br from-gray-800/80 to-gray-900'
                      : 'border-[#dad8e5] bg-gradient-to-br from-emerald-50/90 to-white'
                  }`}
                >
                  <p className="mb-1 text-[10px] uppercase tracking-wide text-[#3a3a3a]/65 dark:text-white/55">
                    {t('gym.statistics.individual.schede.matrixInsightTrend')}
                  </p>
                  <div className="flex items-center gap-2">
                    {insights.overallTrend === 'up' && (
                      <TrendingUp className="h-6 w-6 shrink-0 text-emerald-500 dark:text-emerald-400" aria-hidden />
                    )}
                    {insights.overallTrend === 'down' && (
                      <TrendingDown className="h-6 w-6 shrink-0 text-rose-500 dark:text-rose-400" aria-hidden />
                    )}
                    {insights.overallTrend === 'flat' && (
                      <Minus className="h-6 w-6 shrink-0 text-amber-500 dark:text-amber-300" aria-hidden />
                    )}
                    <span className="text-lg font-semibold text-[#3a3a3a] dark:text-white">
                      {insights.overallTrend === 'up' && t('gym.statistics.individual.schede.matrixTrendUp')}
                      {insights.overallTrend === 'down' && t('gym.statistics.individual.schede.matrixTrendDown')}
                      {insights.overallTrend === 'flat' && t('gym.statistics.individual.schede.matrixTrendFlat')}
                    </span>
                  </div>
                  {insights.pctSession != null && matrixData.workouts.length >= 2 && (
                    <p className="mt-2 text-xs text-[#3a3a3a]/75 dark:text-white/65">
                      {t('gym.statistics.individual.schede.matrixVsFirst', {
                        pct: `${insights.pctSession > 0 ? '+' : ''}${insights.pctSession.toFixed(1)}`
                      })}
                    </p>
                  )}
                </div>
                <div
                  className={`rounded-xl border p-4 ${
                    isDark ? 'border-white/10 bg-gray-900/90' : 'border-[#dad8e5] bg-[#eeebf5]/80'
                  }`}
                >
                  <p className="mb-1 text-[10px] uppercase tracking-wide text-[#3a3a3a]/65 dark:text-white/55">
                    {t('gym.statistics.individual.schede.matrixSessions')}
                  </p>
                  <p className="text-2xl font-bold tabular-nums text-[#3a3a3a] dark:text-white">
                    {matrixData.workouts.length}
                  </p>
                  <p className="mt-1 text-xs text-[#3a3a3a]/65 dark:text-white/55">
                    {t('gym.statistics.individual.schede.matrixSeriesCount', { n: matrixData.rows.length })}
                  </p>
                </div>
                <div
                  className={`rounded-xl border p-4 sm:col-span-1 ${
                    isDark ? 'border-white/10 bg-gray-900/90' : 'border-[#dad8e5] bg-[#eeebf5]/80'
                  }`}
                >
                  <p className="mb-2 text-[10px] uppercase tracking-wide text-[#3a3a3a]/65 dark:text-white/55">
                    {t('gym.statistics.individual.schede.matrixSparkTitle')}
                  </p>
                  <div className="h-[100px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={sparkData} margin={{ top: 4, right: 4, left: -18, bottom: 0 }}>
                        <CartesianGrid stroke={gridStroke} strokeDasharray="3 3" vertical={false} />
                        <XAxis
                          dataKey="label"
                          tick={{ fontSize: 9, fill: axisTick }}
                          interval={0}
                          angle={-20}
                          textAnchor="end"
                          height={36}
                        />
                        <YAxis tick={{ fontSize: 9, fill: axisTick }} width={36} domain={[0, 'auto']} />
                        <Tooltip
                          formatter={(v) => [typeof v === 'number' ? v.toLocaleString('it-IT') : v, t('gym.statistics.individual.schede.matrixTotalMetric')]}
                          labelStyle={{ fontSize: 11, color: isDark ? '#faf9fc' : '#3a3a3a' }}
                          contentStyle={
                            isDark
                              ? { backgroundColor: '#1f2937', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px' }
                              : { backgroundColor: '#fff', border: '1px solid #dad8e5', borderRadius: '8px' }
                          }
                        />
                        <Line
                          type="monotone"
                          dataKey="total"
                          stroke={sparkStroke}
                          strokeWidth={2}
                          dot={{ r: 3, fill: sparkStroke }}
                          name={t('gym.statistics.individual.schede.matrixTotalMetric')}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              <p className="flex items-start gap-2 text-xs text-[#3a3a3a]/70 dark:text-white/60">
                <span className="mt-0.5 inline-block h-3 w-3 shrink-0 rounded-sm bg-emerald-200 dark:bg-emerald-800/60" aria-hidden />
                <span>{t('gym.statistics.individual.schede.matrixLegendUp')}</span>
                <span className="ml-2 mt-0.5 inline-block h-3 w-3 shrink-0 rounded-sm bg-rose-200 dark:bg-rose-800/60" aria-hidden />
                <span>{t('gym.statistics.individual.schede.matrixLegendDown')}</span>
              </p>

              <div className="overflow-x-auto rounded-lg border border-[#dad8e5] dark:border-white/10">
                <table className="min-w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-[#dad8e5] bg-[#eeebf5] dark:border-white/10 dark:bg-gray-900">
                      <th className="sticky left-0 z-10 border-r border-[#dad8e5] bg-[#eeebf5] px-3 py-2 font-semibold whitespace-nowrap text-[#3a3a3a] dark:border-white/10 dark:bg-gray-900 dark:text-white/90">
                        {t('gym.statistics.individual.schede.seriesColumn')}
                      </th>
                      {matrixData.workouts.map((w) => (
                        <th
                          key={w.allenamento_id}
                          className="min-w-[88px] whitespace-nowrap px-3 py-2 font-semibold text-[#3a3a3a] dark:text-white/85"
                        >
                          {w.date_label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {matrixData.rows?.map((row, ri) => {
                      const rt = insights.rowTrends[ri] ?? 'flat';
                      return (
                        <tr key={row.serie_number} className="border-b border-[#dad8e5]/80 dark:border-white/10">
                          <td className="sticky left-0 z-10 border-r border-[#dad8e5] bg-[#faf9fc] px-3 py-2 font-medium whitespace-nowrap text-[#3a3a3a] dark:border-white/10 dark:bg-gray-800 dark:text-white/95">
                            <span className="inline-flex items-center gap-1.5">
                              #{row.serie_number}
                              {rt === 'up' && (
                                <span title={t('gym.statistics.individual.schede.matrixRowTrendUp')} className="text-emerald-500 dark:text-emerald-400">
                                  <TrendingUp className="h-3.5 w-3.5" aria-hidden />
                                </span>
                              )}
                              {rt === 'down' && (
                                <span title={t('gym.statistics.individual.schede.matrixRowTrendDown')} className="text-rose-500 dark:text-rose-400">
                                  <TrendingDown className="h-3.5 w-3.5" aria-hidden />
                                </span>
                              )}
                              {rt === 'flat' && matrixData.workouts.length > 1 && (
                                <span title={t('gym.statistics.individual.schede.matrixRowTrendFlat')} className="text-[#3a3a3a]/45 dark:text-white/45">
                                  <Minus className="h-3.5 w-3.5" aria-hidden />
                                </span>
                              )}
                            </span>
                          </td>
                          {row.cells.map((cell, i) => {
                            const prev = i > 0 ? row.cells[i - 1] : null;
                            const deltaCls = i > 0 ? cellDeltaClass(prev, cell, exMeta) : '';
                            const deltaTitle = i > 0 ? formatCellDeltaTitle(prev, cell, exMeta) : undefined;
                            return (
                              <td
                                key={i}
                                title={deltaTitle}
                                className={`whitespace-nowrap px-3 py-2 tabular-nums text-[#3a3a3a] transition-colors dark:text-white/90 ${deltaCls}`}
                              >
                                {formatMatrixCell(cell, exMeta)}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Prima vs ultima sessione sul KPI principale (volume, rip totali, secondi). Non usa >= così 45 vs 45 è stabile. */
function getExerciseSessionTrend(trend, { isBodyweight, isNormal, isIsometric, isCardio }) {
  if (!trend || trend.length < 2) return null;
  const first = trend[0];
  const last = trend[trend.length - 1];
  const epsRel = 0.008;

  if (isBodyweight) {
    const a = first.total_reps ?? 0;
    const b = last.total_reps ?? 0;
    if (b > a) return 'up';
    if (b < a) return 'down';
    return 'flat';
  }
  if (isNormal && !isBodyweight) {
    const a = Number(first.volume ?? 0);
    const b = Number(last.volume ?? 0);
    const scale = Math.max(Math.abs(a), Math.abs(b), 1);
    if (Math.abs(b - a) < scale * epsRel) return 'flat';
    if (b > a) return 'up';
    return 'down';
  }
  if (isIsometric || isCardio) {
    const a = first.total_seconds ?? 0;
    const b = last.total_seconds ?? 0;
    const scale = Math.max(a, b, 1);
    if (Math.abs(b - a) < Math.max(1, scale * epsRel)) return 'flat';
    if (b > a) return 'up';
    return 'down';
  }
  return null;
}

/** Card esperta: andamento per tipo (normale = volume+1RM; isometrico = secondi; cardio = minuti) */
function ExerciseTrendCard({ exercise, userId }) {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [detailOpen, setDetailOpen] = useState(false);
  const { nome, trend, first_date, last_date, scheda_esercizio_id } = exercise;
  const hasData = trend && trend.length > 0;
  const exerciseType = getExerciseType(exercise);
  const isNormal = exerciseType === EXERCISE_TYPES.NORMAL;
  const isIsometric = exerciseType === EXERCISE_TYPES.ISOMETRIC;
  const isCardio = exerciseType === EXERCISE_TYPES.CARDIO;
  // Esercizio a corpo libero: normale, non cardio/isometrico, ma peso sempre 0 → mostriamo ripetizioni
  const isBodyweight = isNormal && hasData && trend.every((p) => (p.max_weight ?? 0) === 0);

  const chartData = useMemo(() => {
    const base = trend || [];
    if (isCardio) {
      return base.map((p) => ({ ...p, volume_rounded: Math.round(p.volume ?? 0), duration_min: Math.round((p.total_seconds ?? 0) / 60) }));
    }
    if (isIsometric) {
      return base.map((p) => ({ ...p, volume_rounded: Math.round(p.volume ?? 0), total_sec: p.total_seconds ?? 0, max_sec: p.max_seconds ?? 0 }));
    }
    return base.map((p) => ({ ...p, volume_rounded: Math.round(p.volume ?? 0), total_reps: p.total_reps ?? 0 }));
  }, [trend, isNormal, isIsometric, isCardio]);

  const valid1rms = hasData ? trend.map((p) => p.estimated_1rm).filter((v) => v != null) : [];
  const best1rm = valid1rms.length > 0 ? Math.max(...valid1rms) : null;
  const lastVolume = hasData && trend.length > 0 ? trend[trend.length - 1].volume : null;
  const last1rm = hasData && trend.length > 0 ? trend[trend.length - 1].estimated_1rm : null;
  const lastTotalReps = hasData && trend.length > 0 ? trend[trend.length - 1].total_reps : null;
  const lastMaxReps = hasData && trend.length > 0 ? trend[trend.length - 1].max_reps : null;
  const bestTotalReps = hasData && trend.length > 0 ? Math.max(...trend.map((p) => p.total_reps ?? 0)) : null;
  const bestMaxReps = hasData && trend.length > 0 ? Math.max(...trend.map((p) => p.max_reps ?? 0)) : null;
  const lastTotalSec = hasData && trend.length > 0 ? trend[trend.length - 1].total_seconds : null;
  const lastMaxSec = hasData && trend.length > 0 ? trend[trend.length - 1].max_seconds : null;
  const bestMaxSec = hasData && trend.length > 0 ? Math.max(...trend.map((p) => p.max_seconds ?? 0)) : null;
  const sessionTrend = useMemo(
    () => (hasData ? getExerciseSessionTrend(trend, { isBodyweight, isNormal, isIsometric, isCardio }) : null),
    [trend, hasData, isBodyweight, isNormal, isIsometric, isCardio]
  );

  const gradId = `grad-${scheda_esercizio_id ?? 'ex'}`;

  return (
    <div
      className={`overflow-hidden rounded-xl border shadow-sm transition-shadow hover:shadow-md ${
        isDark
          ? 'border-white/10 bg-gray-900/70'
          : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800'
      }`}
    >
      <div
        className={`flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3 ${
          isDark ? 'border-white/10' : 'border-gray-100 dark:border-gray-700'
        }`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <Dumbbell className="h-4 w-4 text-indigo-500 flex-shrink-0" />
          <h3 className="font-semibold text-gray-900 dark:text-white truncate">{nome}</h3>
          {(isIsometric || isCardio) && (
            <span className="text-[10px] uppercase tracking-wide text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 px-1.5 py-0.5 rounded">
              {isCardio ? t('gym.statistics.individual.schede.typeCardio') : t('gym.statistics.individual.schede.typeIsometric')}
            </span>
          )}
          {isBodyweight && (
            <span className="text-[10px] uppercase tracking-wide text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-900/30 px-1.5 py-0.5 rounded">
              {t('gym.statistics.individual.schede.typeBodyweight')}
            </span>
          )}
          {userId && (
            <button
              type="button"
              onClick={() => setDetailOpen(true)}
              className="text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:underline shrink-0"
            >
              {t('gym.statistics.individual.schede.detailLink')}
            </button>
          )}
        </div>
        <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
          {first_date && last_date && (
            <span className="tabular-nums">
              {new Date(first_date).toLocaleDateString('it-IT')} → {new Date(last_date).toLocaleDateString('it-IT')}
            </span>
          )}
          {hasData && <span>{trend.length} sessioni</span>}
        </div>
      </div>
      <div className="p-4">
        {!hasData ? (
          <div className="h-32 flex items-center justify-center text-gray-400 dark:text-gray-500 text-sm">
            {t('gym.statistics.individual.schede.noDataForExercise')}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
              {isNormal && !isBodyweight && lastVolume != null && (
                <div className="rounded-lg bg-gray-50 dark:bg-gray-900/50 px-3 py-2">
                  <p className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">{t('gym.statistics.individual.schede.lastVolume')}</p>
                  <p className="text-sm font-bold text-gray-900 dark:text-white tabular-nums">{Math.round(lastVolume).toLocaleString()} kg</p>
                </div>
              )}
              {isNormal && !isBodyweight && last1rm != null && (
                <div className="rounded-lg bg-gray-50 dark:bg-gray-900/50 px-3 py-2">
                  <p className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">1RM (ultima)</p>
                  <p className="text-sm font-bold text-indigo-600 dark:text-indigo-400 tabular-nums">{last1rm} kg</p>
                </div>
              )}
              {isNormal && !isBodyweight && best1rm != null && (
                <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/20 px-3 py-2">
                  <p className="text-[10px] uppercase tracking-wide text-emerald-600 dark:text-emerald-400">{t('gym.statistics.individual.schede.best1rm')}</p>
                  <p className="text-sm font-bold text-emerald-700 dark:text-emerald-300 tabular-nums">{best1rm} kg</p>
                </div>
              )}
              {isBodyweight && (
                <>
                  {lastTotalReps != null && (
                    <div className="rounded-lg bg-violet-50 dark:bg-violet-900/20 px-3 py-2">
                      <p className="text-[10px] uppercase tracking-wide text-violet-600 dark:text-violet-400">{t('gym.statistics.individual.schede.lastTotalReps')}</p>
                      <p className="text-sm font-bold text-violet-700 dark:text-violet-300 tabular-nums">{lastTotalReps}</p>
                    </div>
                  )}
                  {lastMaxReps != null && (
                    <div className="rounded-lg bg-gray-50 dark:bg-gray-900/50 px-3 py-2">
                      <p className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">{t('gym.statistics.individual.schede.lastMaxReps')}</p>
                      <p className="text-sm font-bold text-gray-900 dark:text-white tabular-nums">{lastMaxReps}</p>
                    </div>
                  )}
                  {bestTotalReps != null && bestTotalReps > 0 && (
                    <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/20 px-3 py-2">
                      <p className="text-[10px] uppercase tracking-wide text-emerald-600 dark:text-emerald-400">{t('gym.statistics.individual.schede.bestTotalReps')}</p>
                      <p className="text-sm font-bold text-emerald-700 dark:text-emerald-300 tabular-nums">{bestTotalReps}</p>
                    </div>
                  )}
                </>
              )}
              {isIsometric && (
                <>
                  {lastTotalSec != null && (
                    <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 px-3 py-2">
                      <p className="text-[10px] uppercase tracking-wide text-amber-600 dark:text-amber-400">{t('gym.statistics.individual.schede.lastTotalTime')}</p>
                      <p className="text-sm font-bold text-amber-700 dark:text-amber-300 tabular-nums">{lastTotalSec} s</p>
                    </div>
                  )}
                  {lastMaxSec != null && (
                    <div className="rounded-lg bg-gray-50 dark:bg-gray-900/50 px-3 py-2">
                      <p className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">{t('gym.statistics.individual.schede.lastHold')}</p>
                      <p className="text-sm font-bold text-gray-900 dark:text-white tabular-nums">{lastMaxSec} s</p>
                    </div>
                  )}
                  {bestMaxSec != null && bestMaxSec > 0 && (
                    <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/20 px-3 py-2">
                      <p className="text-[10px] uppercase tracking-wide text-emerald-600 dark:text-emerald-400">{t('gym.statistics.individual.schede.bestHold')}</p>
                      <p className="text-sm font-bold text-emerald-700 dark:text-emerald-300 tabular-nums">{bestMaxSec} s</p>
                    </div>
                  )}
                </>
              )}
              {isCardio && (
                <>
                  {lastTotalSec != null && (
                    <div className="rounded-lg bg-rose-50 dark:bg-rose-900/20 px-3 py-2">
                      <p className="text-[10px] uppercase tracking-wide text-rose-600 dark:text-rose-400">{t('gym.statistics.individual.schede.lastDuration')}</p>
                      <p className="text-sm font-bold text-rose-700 dark:text-rose-300 tabular-nums">{Math.round(lastTotalSec / 60)} min</p>
                    </div>
                  )}
                  {bestMaxSec != null && bestMaxSec > 0 && (
                    <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/20 px-3 py-2">
                      <p className="text-[10px] uppercase tracking-wide text-emerald-600 dark:text-emerald-400">{t('gym.statistics.individual.schede.bestSingleBlock')}</p>
                      <p className="text-sm font-bold text-emerald-700 dark:text-emerald-300 tabular-nums">{Math.round(bestMaxSec / 60)} min</p>
                    </div>
                  )}
                </>
              )}
              <div className="rounded-lg bg-gray-50 dark:bg-gray-900/50 px-3 py-2 flex items-center gap-1">
                {sessionTrend === 'up' && <TrendingUp className="h-4 w-4 text-emerald-500 shrink-0" />}
                {sessionTrend === 'down' && <TrendingDown className="h-4 w-4 text-rose-500 shrink-0" />}
                {sessionTrend === 'flat' && <Minus className="h-4 w-4 text-gray-400 shrink-0" />}
                {sessionTrend == null && <span className="text-gray-400 shrink-0">—</span>}
                <p className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Trend</p>
                <p className="text-sm font-bold text-gray-900 dark:text-white">
                  {sessionTrend === 'up' && t('gym.statistics.individual.schede.trendUp')}
                  {sessionTrend === 'down' && t('gym.statistics.individual.schede.trendDown')}
                  {sessionTrend === 'flat' && t('gym.statistics.individual.schede.trendFlat')}
                  {sessionTrend == null && '—'}
                </p>
              </div>
            </div>
            <div
              className={`h-52 rounded-lg ${
                isDark ? 'border border-white/10 bg-gray-900 p-2' : ''
              }`}
            >
              {isBodyweight && (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id={`${gradId}-reps`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={isDark ? STATS_CHART_VIOLET : '#7c3aed'} stopOpacity={0.4} />
                        <stop offset="100%" stopColor={isDark ? STATS_CHART_VIOLET : '#7c3aed'} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={isDark ? 'rgba(255,255,255,0.14)' : '#e5e7eb'}
                    />
                    <XAxis dataKey="date_label" tick={{ fontSize: 10 }} />
                    <YAxis yAxisId="reps" tick={{ fontSize: 10 }} allowDecimals={false} />
                    <YAxis yAxisId="max" orientation="right" tick={{ fontSize: 10 }} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{ borderRadius: 8, border: '1px solid var(--border)' }}
                      formatter={(value, name) => {
                        if (name === 'total_reps') return [value != null ? value : '—', t('gym.statistics.individual.schede.totalReps')];
                        if (name === 'max_reps') return [value != null ? value : '—', t('gym.statistics.individual.schede.maxRepsSet')];
                        return [value, name];
                      }}
                      labelFormatter={(label) => label}
                    />
                    <Legend />
                    <Area
                      yAxisId="reps"
                      type="monotone"
                      dataKey="total_reps"
                      name={t('gym.statistics.individual.schede.totalReps')}
                      stroke={isDark ? STATS_CHART_VIOLET : '#7c3aed'}
                      fill={`url(#${gradId}-reps)`}
                      strokeWidth={2}
                    />
                    <Line
                      yAxisId="max"
                      type="monotone"
                      dataKey="max_reps"
                      name={t('gym.statistics.individual.schede.maxRepsSet')}
                      stroke={isDark ? '#a78bfa' : '#5b21b6'}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      connectNulls
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
              {isNormal && !isBodyweight && (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id={`${gradId}-vol`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={isDark ? STATS_CHART_VIOLET : '#6366f1'} stopOpacity={0.4} />
                        <stop offset="100%" stopColor={isDark ? STATS_CHART_VIOLET : '#6366f1'} stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id={`${gradId}-rm`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#10b981" stopOpacity={0.3} />
                        <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={isDark ? 'rgba(255,255,255,0.14)' : '#e5e7eb'}
                    />
                    <XAxis dataKey="date_label" tick={{ fontSize: 10, fill: isDark ? '#d1d5db' : undefined }} />
                    <YAxis yAxisId="vol" tick={{ fontSize: 10, fill: isDark ? '#d1d5db' : undefined }} allowDecimals={false} />
                    <YAxis yAxisId="rm" orientation="right" tick={{ fontSize: 10, fill: isDark ? '#d1d5db' : undefined }} allowDecimals={false} />
                    <Tooltip
                      contentStyle={
                        isDark
                          ? { borderRadius: 8, border: '1px solid rgba(255,255,255,0.15)', backgroundColor: '#1f1a3d', color: '#fff' }
                          : { borderRadius: 8, border: '1px solid var(--border)' }
                      }
                      formatter={(value, name) => {
                        if (name === 'volume_rounded' || name === 'volume') return [value != null ? Number(value).toLocaleString() : '—', t('gym.statistics.individual.schede.volumeKg')];
                        if (name === 'estimated_1rm') return [value != null ? `${value} kg` : '—', '1RM stimata'];
                        return [value, name];
                      }}
                      labelFormatter={(label) => label}
                    />
                    <Legend />
                    <Area
                      yAxisId="vol"
                      type="monotone"
                      dataKey="volume_rounded"
                      name={t('gym.statistics.individual.schede.volumeKg')}
                      stroke={isDark ? STATS_CHART_VIOLET : '#6366f1'}
                      fill={`url(#${gradId}-vol)`}
                      strokeWidth={2}
                    />
                    <Line yAxisId="rm" type="monotone" dataKey="estimated_1rm" name="1RM stimata" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                  </AreaChart>
                </ResponsiveContainer>
              )}
              {isIsometric && (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id={`${gradId}-sec`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#d97706" stopOpacity={0.4} />
                        <stop offset="100%" stopColor="#d97706" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                    <XAxis dataKey="date_label" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{ borderRadius: 8, border: '1px solid var(--border)' }}
                      formatter={(value, name) => {
                        if (name === 'total_sec') return [value != null ? `${value} s` : '—', t('gym.statistics.individual.schede.totalTimeSec')];
                        if (name === 'max_sec') return [value != null ? `${value} s` : '—', t('gym.statistics.individual.schede.maxHoldSec')];
                        return [value, name];
                      }}
                      labelFormatter={(label) => label}
                    />
                    <Legend />
                    <Area type="monotone" dataKey="total_sec" name={t('gym.statistics.individual.schede.totalTimeSec')} stroke="#d97706" fill={`url(#${gradId}-sec)`} strokeWidth={2} />
                    <Line type="monotone" dataKey="max_sec" name={t('gym.statistics.individual.schede.maxHoldSec')} stroke="#b45309" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                  </AreaChart>
                </ResponsiveContainer>
              )}
              {isCardio && (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id={`${gradId}-min`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#e11d48" stopOpacity={0.4} />
                        <stop offset="100%" stopColor="#e11d48" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                    <XAxis dataKey="date_label" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{ borderRadius: 8, border: '1px solid var(--border)' }}
                      formatter={(value, name) => {
                        if (name === 'duration_min') return [value != null ? `${value} min` : '—', t('gym.statistics.individual.schede.durationMin')];
                        return [value, name];
                      }}
                      labelFormatter={(label) => label}
                    />
                    <Legend />
                    <Area type="monotone" dataKey="duration_min" name={t('gym.statistics.individual.schede.durationMin')} stroke="#e11d48" fill={`url(#${gradId}-min)`} strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </>
        )}
      </div>
      {userId && (
        <ExerciseSeriesDetailModal
          open={detailOpen}
          onClose={() => setDetailOpen(false)}
          userId={userId}
          exercise={exercise}
        />
      )}
    </div>
  );
}

/** Placeholder figura stile Vitruviano: silhouette con zone gruppi muscolari */
function VitruvianPlaceholder() {
  return (
    <svg
      viewBox="0 0 120 220"
      className="w-32 h-auto text-gray-300 dark:text-gray-600"
      aria-hidden
    >
      <ellipse cx="60" cy="18" rx="14" ry="12" fill="currentColor" opacity="0.4" />
      <path
        d="M 60 30 L 60 55 Q 55 75 50 95 L 48 130 L 60 165 L 72 130 L 70 95 Q 65 75 60 55 Z"
        fill="currentColor"
        opacity="0.25"
      />
      <path d="M 60 55 L 60 95" stroke="currentColor" strokeWidth="2" opacity="0.5" />
      <path d="M 35 55 L 60 70 L 85 55" stroke="currentColor" strokeWidth="1.5" opacity="0.4" />
      <path d="M 35 95 L 60 110 L 85 95" stroke="currentColor" strokeWidth="1.5" opacity="0.4" />
      <path d="M 60 165 L 45 210 L 60 205 L 75 210 Z" fill="currentColor" opacity="0.2" />
      <text x="60" y="55" textAnchor="middle" fontSize="6" fill="currentColor" opacity="0.6">
        spalle
      </text>
      <text x="60" y="95" textAnchor="middle" fontSize="6" fill="currentColor" opacity="0.6">
        core
      </text>
      <text x="60" y="140" textAnchor="middle" fontSize="5" fill="currentColor" opacity="0.5">
        arti
      </text>
    </svg>
  );
}
